import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CouponClipService } from '../coupon-clip.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';

export interface CartItem {
  product: any;
  quantity: number;
}

export interface ServerCart {
  _id?: string;
  userId?: string;
  items: any[];
  status: string;
  totalValue: number;
  totalSavings: number;
  itemCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private cartItems: CartItem[] = [];
  private cartItemsSubject = new BehaviorSubject<CartItem[]>([]);
  public cartItems$ = this.cartItemsSubject.asObservable();

  private cartCountSubject = new BehaviorSubject<number>(0);
  public cartCount$ = this.cartCountSubject.asObservable();

  private apiUrl = environment.apiUrl + '/api/cart';

  constructor(
    private couponClipService: CouponClipService,
    private http: HttpClient
  ) {
    this.initializeCart();
  }

  private initializeCart() {
    // Load local cart first for immediate UI update
    this.loadLocalCart();

    // Sync with server if user is logged in
    const user = this.getUser();
    if (user && navigator.onLine) {
      this.syncFromServer();
    }
  }

  private getUser(): any {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  private loadLocalCart() {
    const savedCart = localStorage.getItem('shopping_cart');
    if (savedCart) {
      try {
        this.cartItems = JSON.parse(savedCart);
        this.updateCartObservables();
      } catch (e) {
        console.error('Error loading cart from localStorage', e);
        this.cartItems = [];
      }
    }
  }

  private saveLocalCart() {
    localStorage.setItem('shopping_cart', JSON.stringify(this.cartItems));
    this.updateCartObservables();
  }

  private updateCartObservables() {
    this.cartItemsSubject.next(this.cartItems);
    const totalCount = this.cartItems.reduce((sum, item) => sum + item.quantity, 0);
    this.cartCountSubject.next(totalCount);
  }

  private async syncFromServer() {
    try {
      const response = await this.http.get<any>(this.apiUrl).toPromise();

      if (response.cart && response.cart.items) {
        // Convert server cart items to local format
        const serverItems: CartItem[] = response.cart.items.map((item: any) => ({
          product: item.productId,
          quantity: item.quantity
        }));

        // Replace local cart with server cart (server is source of truth)
        this.cartItems = serverItems;
        this.saveLocalCart();
      }
    } catch (error) {
      console.error('Error syncing cart from server:', error);
      // Continue with local cart if server sync fails
    }
  }

  async addToCart(product: any, quantity: number = 1) {
    // Validate quantity
    if (quantity < 1) {
      console.warn('⚠️ Quantity must be at least 1');
      quantity = 1;
    }
    if (quantity > 50) {
      console.warn(`⚠️ Quantity capped at 50`);
      quantity = 50;
    }

    const existingItem = this.cartItems.find(item => item.product._id === product._id);

    if (existingItem) {
      const newQuantity = existingItem.quantity + quantity;
      if (newQuantity > 50) {
        console.warn(`⚠️ Total quantity capped at 50`);
        existingItem.quantity = 50;
      } else {
        existingItem.quantity = newQuantity;
      }
    } else {
      this.cartItems.push({ product, quantity });
    }

    this.saveLocalCart();

    // Sync with server
    if (navigator.onLine) {
      try {
        await this.http.post(
          `${this.apiUrl}/add`,
          {
            productId: product._id,
            quantity
          }
        ).toPromise();
      } catch (error) {
        console.error('Error syncing add to cart with server:', error);
      }
    }
  }

  async removeFromCart(productId: string) {
    this.cartItems = this.cartItems.filter(item => item.product._id !== productId);
    this.saveLocalCart();

    // Sync with server
    if (navigator.onLine) {
      try {
        await this.http.delete(
          `${this.apiUrl}/remove/${productId}`
        ).toPromise();
      } catch (error) {
        console.error('Error syncing remove from cart with server:', error);
      }
    }
  }

  async updateQuantity(productId: string, quantity: number) {
    // Validate quantity
    if (quantity < 0) {
      console.warn('⚠️ Negative quantity, setting to 0');
      quantity = 0;
    }
    if (quantity > 50) {
      console.warn(`⚠️ Quantity capped at 50`);
      quantity = 50;
    }

    const item = this.cartItems.find(item => item.product._id === productId);
    if (item) {
      if (quantity <= 0) {
        await this.removeFromCart(productId);
      } else {
        item.quantity = quantity;
        this.saveLocalCart();

        // Sync with server
        if (navigator.onLine) {
          try {
            await this.http.put(
              `${this.apiUrl}/update`,
              {
                productId,
                quantity
              }
            ).toPromise();
          } catch (error) {
            console.error('Error syncing quantity update with server:', error);
          }
        }
      }
    }
  }

  async clearCart() {
    this.cartItems = [];
    this.saveLocalCart();

    // Sync with server
    if (navigator.onLine) {
      try {
        await this.http.delete(`${this.apiUrl}/clear`).toPromise();
      } catch (error) {
        console.error('Error syncing clear cart with server:', error);
      }
    }
  }

  getCart(): CartItem[] {
    return this.cartItems;
  }

  getCartTotal(): number {
    return this.cartItems.reduce((total, item) => {
      const price = item.product.salePrice || item.product.price;
      return total + (price * item.quantity);
    }, 0);
  }

  getCartTotalWithCoupons(): number {
    return this.cartItems.reduce((total, item) => {
      const price = item.product.salePrice || item.product.price;

      // Apply clipped coupon if available
      const clippedCoupon = this.couponClipService.getClippedCoupon(item.product._id);
      if (clippedCoupon) {
        const savings = this.couponClipService.calculateSavings(price, item.product._id);
        return total + ((price - savings) * item.quantity);
      }

      return total + (price * item.quantity);
    }, 0);
  }

  getTotalSavingsFromCoupons(): number {
    return this.cartItems.reduce((totalSavings, item) => {
      const clippedCoupon = this.couponClipService.getClippedCoupon(item.product._id);
      if (clippedCoupon) {
        const price = item.product.salePrice || item.product.price;
        const savings = this.couponClipService.calculateSavings(price, item.product._id);
        return totalSavings + (savings * item.quantity);
      }
      return totalSavings;
    }, 0);
  }

  getItemPriceWithCoupon(productId: string): number {
    const item = this.cartItems.find(item => item.product._id === productId);
    if (!item) return 0;

    const price = item.product.salePrice || item.product.price;
    return this.couponClipService.getFinalPrice(price, productId);
  }

  getItemSavings(productId: string): number {
    const item = this.cartItems.find(item => item.product._id === productId);
    if (!item) return 0;

    const price = item.product.salePrice || item.product.price;
    return this.couponClipService.calculateSavings(price, productId);
  }

  hasClippedCoupon(productId: string): boolean {
    return this.couponClipService.isCouponClipped(productId);
  }

  getCartItemCount(): number {
    return this.cartItems.reduce((sum, item) => sum + item.quantity, 0);
  }

  isInCart(productId: string): boolean {
    return this.cartItems.some(item => item.product._id === productId);
  }

  getItemQuantity(productId: string): number {
    const item = this.cartItems.find(item => item.product._id === productId);
    return item ? item.quantity : 0;
  }
}
